import type { CombinedPaymentVendor } from "~/features/products/combined-payment.utils";
import { isCombinedPaymentEnabled } from "~/features/products/combined-payment.utils";

/**
 * Whether checkout offers the cross-sell step to this basket at all.
 *
 * Two independent gates. `enableCheckoutOtcCrossSell` gates the feature as a
 * whole. `enableOtcCrossSellForCombinedPayment` governs one exception to it: a
 * pharmacy that takes one combined payment is kept out of cross-sell, so that an
 * accessory line can never join a single charge covering the cannabis order and
 * the prescription fee — the partial-refund case Phase 1 set out to avoid
 * (FR-040).
 *
 * That exception has its own flag rather than riding on the cross-sell flag,
 * because the two answer different questions and are expected to move at
 * different times: the cross-sell flag opens at GTM, while including these
 * pharmacies is not expected to be prioritised for months. Turning this one on
 * is what includes them, so that decision costs a flag flip rather than a code
 * change.
 *
 * One derivation rather than a check at each site, because four places decide
 * whether this step is reached — its own loader, the awareness step, the
 * questionnaire-prefill path, and the cart provider that mounts only for
 * sessions that will reach it — and the defect this corrects is that all four
 * honoured the cross-sell flag while none honoured the exception.
 *
 * Capability is read through `isCombinedPaymentEnabled` (payments FR-013), which
 * answers `false` for an absent field, so a cart predating it is not excluded.
 */
export function isCrossSellStepOffered({
	cart,
	enableCheckoutOtcCrossSell,
	enableOtcCrossSellForCombinedPayment,
}: {
	cart: { vendor?: CombinedPaymentVendor | null } | null | undefined;
	enableCheckoutOtcCrossSell: boolean;
	enableOtcCrossSellForCombinedPayment: boolean;
}): boolean {
	if (!enableCheckoutOtcCrossSell) {
		return false;
	}

	if (enableOtcCrossSellForCombinedPayment) {
		return true;
	}

	return !isCombinedPaymentEnabled(cart?.vendor);
}
